import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ShieldAlert, Ban, CheckCircle, Mail, ArrowLeft, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Terms = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {language === 'it' ? 'Torna alla home' : 'Back to home'}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                <ShieldAlert className="h-7 w-7 text-red-600" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-stone-900">
                  {language === 'it' ? 'Regole della Community' : 'Community Rules'}
                </h1>
                <p className="text-stone-600">
                  {language === 'it' ? 'Termini di utilizzo della piattaforma Moneyfor' : 'Moneyfor platform terms of use'}
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                <strong>{language === 'it' ? 'AVVISO IMPORTANTE:' : 'IMPORTANT NOTICE:'}</strong>{' '}
                {language === 'it' 
                  ? 'La violazione di queste regole comporta la rimozione immediata e permanente dell\'account senza possibilità di appello.'
                  : 'Violation of these rules results in immediate and permanent account removal without possibility of appeal.'}
              </p>
            </div>
          </div>

          {/* Prohibited Content */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Ban className="h-6 w-6 text-red-600" />
              <h2 className="font-heading text-2xl font-semibold text-stone-900">
                {language === 'it' ? 'Contenuti Assolutamente Vietati' : 'Strictly Prohibited Content'}
              </h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">
                  {language === 'it' ? '🚫 Contenuti Sessuali e Prestazioni' : '🚫 Sexual Content and Services'}
                </h3>
                <ul className="text-red-800 space-y-2">
                  <li>• {language === 'it' ? 'Immagini o video con nudità parziale o totale' : 'Images or videos with partial or total nudity'}</li>
                  <li>• {language === 'it' ? 'Contenuti a sfondo erotico o sessualmente espliciti' : 'Erotic or sexually explicit content'}</li>
                  <li>• {language === 'it' ? 'Offerte di prestazioni sessuali di qualsiasi tipo' : 'Offers of sexual services of any kind'}</li>
                  <li>• {language === 'it' ? 'Messaggi con proposte sessuali agli utenti' : 'Messages with sexual proposals to users'}</li>
                  <li>• {language === 'it' ? 'Link a siti per adulti o piattaforme simili' : 'Links to adult sites or similar platforms'}</li>
                </ul>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">
                  {language === 'it' ? '🚫 Truffe e Contenuti Fraudolenti' : '🚫 Scams and Fraudulent Content'}
                </h3>
                <ul className="text-red-800 space-y-2">
                  <li>• {language === 'it' ? 'Richieste di aiuto false o inventate' : 'False or fabricated help requests'}</li>
                  <li>• {language === 'it' ? 'Uso di foto/video non propri senza autorizzazione' : 'Use of photos/videos not owned without authorization'}</li>
                  <li>• {language === 'it' ? 'Richieste ripetute per la stessa situazione già finanziata' : 'Repeated requests for already funded situations'}</li>
                  <li>• {language === 'it' ? 'Schemi piramidali o catene di Sant\'Antonio' : 'Pyramid schemes or chain letters'}</li>
                </ul>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">
                  {language === 'it' ? '🚫 Altri Contenuti Vietati' : '🚫 Other Prohibited Content'}
                </h3>
                <ul className="text-red-800 space-y-2">
                  <li>• {language === 'it' ? 'Incitamento all\'odio, razzismo, discriminazione' : 'Hate speech, racism, discrimination'}</li>
                  <li>• {language === 'it' ? 'Contenuti violenti o che glorificano la violenza' : 'Violent content or glorification of violence'}</li>
                  <li>• {language === 'it' ? 'Vendita di prodotti illegali' : 'Sale of illegal products'}</li>
                  <li>• {language === 'it' ? 'Spam o pubblicità non autorizzata' : 'Spam or unauthorized advertising'}</li>
                  <li>• {language === 'it' ? 'Molestie o bullismo verso altri utenti' : 'Harassment or bullying of other users'}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Allowed Content */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
              <h2 className="font-heading text-2xl font-semibold text-stone-900">
                {language === 'it' ? 'Contenuti Ammessi' : 'Allowed Content'}
              </h2>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <ul className="text-emerald-800 space-y-2">
                <li>✅ {language === 'it' ? 'Richieste genuine per spese mediche con documentazione' : 'Genuine medical expense requests with documentation'}</li>
                <li>✅ {language === 'it' ? 'Richieste per educazione e formazione' : 'Education and training requests'}</li>
                <li>✅ {language === 'it' ? 'Emergenze familiari verificabili' : 'Verifiable family emergencies'}</li>
                <li>✅ {language === 'it' ? 'Richieste per casa, cibo e beni di prima necessità' : 'Requests for housing, food and basic necessities'}</li>
                <li>✅ {language === 'it' ? 'Avvio di piccole attività per l\'autosufficienza' : 'Starting small businesses for self-sufficiency'}</li>
                <li>✅ {language === 'it' ? 'Foto e video appropriati della propria situazione' : 'Appropriate photos and videos of your situation'}</li>
              </ul>
            </div>
          </div>

          {/* Contact for Support */}
          <div className="bg-emerald-800 rounded-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-6 w-6" />
              <h2 className="font-heading text-2xl font-semibold">
                {language === 'it' ? 'Hai bisogno di assistenza?' : 'Need assistance?'}
              </h2>
            </div>
            <p className="text-emerald-100 mb-6">
              {language === 'it' 
                ? 'Per qualsiasi domanda, segnalazione di contenuti inappropriati o supporto, contattaci:'
                : 'For any questions, reporting inappropriate content or support, contact us:'}
            </p>
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
              <Mail className="h-8 w-8 text-amber-400" />
              <div>
                <p className="text-sm text-emerald-200">Email Assistenza Clienti</p>
                <a 
                  href="mailto:postmaster@moneyfor.it" 
                  className="text-xl font-semibold text-white hover:text-amber-300 transition-colors"
                >
                  postmaster@moneyfor.it
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
